// src/pages/WorkItemPage.js
//
// Universal Work Item Details Page
// =================================
//
// PURPOSE
// -------
// This page renders the "details" view for one universal work item key, for example:
//
//   /work-items/todo%3A123
//   /work-items/leave%3A9223372036854775807
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// This file is aligned with your current frontend/backend architecture:
//
// 1) HTTP is authoritative
//    - details are loaded from GET /work-items/:key
//    - the response is the source of truth for what the user may view
//
// 2) "Seen" is a separate semantic from "chat read"
//    - after successful details load we mark the work item as seen (best-effort)
//    - chat read remains the responsibility of the chat feature
//
// 3) Multi-tab-safe auth policy
//    - NEVER call backend /auth/logout automatically from this page
//    - on auth/session failure (401 / refresh failed / session selector problem)
//      call softLogout() only, because that is local to THIS tab
//
// 4) Embedded chat should not outrun failed details
//    - if details load fails with 403/404/invalid key, there is no benefit in
//      booting embedded chat immediately and producing a second layer of errors
//    - therefore this version renders embedded chat only after details load succeeds
//
// UX GOALS
// --------
// - stable loading / error / success states
// - type-aware rendering for TODO and LEAVE work items
// - safe fallback rendering for future work item types
// - clean abort behavior on key change / unmount
//
// i18n notes:
// - Backend work item keys, entity ids, raw types and payload values remain stable
//   technical data. The frontend translates only the labels/fallbacks it owns.
// - Leave type names are already multilingual payload fields in the backend DTO;
//   this page chooses name_pl/name_en according to the current i18n language and
//   falls back to code only when the localized names are missing.
// - Backend 4xx messages are preserved when provided, because they often contain
//   precise authorization/key/visibility information that should not be hidden.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import AuthContext from "../context/AuthContext";
import { fetchWorkItem } from "../services/workItemService";
import { markWorkItemSeen } from "../services/workItemSeenService";
import { isRequestCanceled } from "../services/httpUtils";
import { formatLeaveBusinessEnd, formatLeaveBusinessStart } from "../utils/leaveDateUtils";

import WorkItemChatPanel from "../components/WorkItemChatPanel";

import "./WorkItemPage.css";

const DEV = process.env.NODE_ENV !== "production";
const EMPTY_VALUE = "—";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Decode the router param safely.
 *
 * The route param contains ":" inside the work item key, so React Router passes
 * it URL-encoded in the URL.
 */
function decodeWorkItemKeyParam(rawKey) {
  try {
    return decodeURIComponent(rawKey || "");
  } catch {
    return String(rawKey || "");
  }
}

/**
 * Return a short, user-facing message for load failures.
 *
 * We prefer backend 4xx messages when available, because those often contain
 * meaningful information such as:
 * - invalid work item key
 * - unsupported work item type
 * - forbidden
 * - not found
 */
function getLoadErrorMessage(err, t) {
  const status = err?.response?.status;
  const backendMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "";

  if (typeof status === "number" && status >= 400 && status < 500 && backendMsg) {
    return String(backendMsg);
  }

  if (status === 403) return t("workItems:details.errors.forbidden");
  if (status === 404) return t("workItems:details.errors.notFound");
  if (status === 400) return t("workItems:details.errors.invalidKey");

  return t("workItems:details.errors.loadFailed");
}

/**
 * Detect auth/session problems that should cause local-only logout.
 *
 * IMPORTANT:
 * - softLogout() is correct here
 * - handleLogout() would be incorrect, because it would clear shared cookies
 */
function isAuthFailure(err) {
  const httpStatus = err?.response?.status;
  const code = err?.code;

  return (
    httpStatus === 401 ||
    code === "SESSION_ID_REQUIRED" ||
    code === "REFRESH_FAILED"
  );
}

/**
 * Convert a value to a human-friendly localized yes/no string.
 */
function yesNo(value, t) {
  return value ? t("common:common.yes") : t("common:common.no");
}

/**
 * Safe date formatting for ISO / date-like values.
 */
function formatDateTime(value, locale) {
  if (!value) return EMPTY_VALUE;

  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return EMPTY_VALUE;

  return d.toLocaleString(locale);
}

/**
 * Safe date-only formatting.
 */
function formatDateOnly(value, locale) {
  if (!value) return EMPTY_VALUE;

  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return EMPTY_VALUE;

  return d.toLocaleDateString(locale);
}

/**
 * Safe display of a user object included by backend.
 */
function formatUserLabel(userLike, t) {
  if (!userLike) return EMPTY_VALUE;

  const fullName = `${userLike.first_name || ""} ${userLike.last_name || ""}`.trim();
  if (fullName && userLike.username) return `${fullName} (${userLike.username})`;
  if (fullName) return fullName;
  if (userLike.username) return userLike.username;
  if (userLike.id != null) return t("workItems:details.values.userNumber", { id: userLike.id });

  return EMPTY_VALUE;
}

/**
 * Safe plain text for meta/debug values.
 */
function safeText(value) {
  if (value == null || value === "") return EMPTY_VALUE;
  return String(value);
}

/**
 * Resolve the localized leave type name from backend-provided multilingual DTO fields.
 */
function getLocalizedLeaveTypeName(leaveType, language, t) {
  if (!leaveType) return t("workItems:details.values.leaveRequest");

  const isPolish = String(language || "").toLowerCase().startsWith("pl");
  const localizedName = isPolish
    ? leaveType.name_pl || leaveType.name_en
    : leaveType.name_en || leaveType.name_pl;

  return localizedName || leaveType.code || t("workItems:details.values.leaveRequest");
}

/**
 * Translate known leave status codes while preserving unknown backend values.
 */
function formatLeaveStatus(status, t) {
  if (status == null || status === "") return EMPTY_VALUE;

  const raw = String(status);
  const normalized = raw.trim().toUpperCase();

  return t(`leaves:status.${normalized}`, { defaultValue: raw });
}


/**
 * Convert a numeric-like value from backend into a finite number.
 *
 * The leave backend stores many values as DECIMAL strings to preserve database
 * precision. This helper lets the UI format those values safely without
 * leaking raw SQL/JSON representation to normal users.
 */
function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format decimal values in a stable but not overly technical way.
 *
 * We intentionally trim trailing zeroes so values such as 22.00 become 22,
 * while real fractions such as 1.50 remain readable as 1.5 when no fixed scale
 * is legally required.
 */
function formatDecimal(value, maxFractionDigits = 2, locale = undefined) {
  const n = toNumberOrNull(value);
  if (n == null) return EMPTY_VALUE;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  }).format(n);
}

/**
 * Format minutes as a civil working-time duration.
 *
 * Examples:
 * - 90    -> 1 h 30 min
 * - 480   -> 8 h
 * - 10560 -> 176 h
 *
 * We keep this deliberately compact because it is shown in summary cards.
 */
function formatMinutesAsHours(minutes, t) {
  const total = Math.round(toNumberOrNull(minutes) || 0);
  if (total <= 0) return EMPTY_VALUE;

  const hours = Math.floor(total / 60);
  const mins = total % 60;

  if (hours > 0 && mins > 0) {
    return t("workItems:details.duration.hoursAndMinutes", { hours, minutes: mins });
  }

  if (hours > 0) {
    return t("workItems:details.duration.hoursOnly", { hours });
  }

  return t("workItems:details.duration.minutesOnly", { minutes: mins });
}

/**
 * Format the raw backend duration as technical minutes.
 *
 * This remains visible as a secondary line because it is useful for support,
 * settlement checks and payroll troubleshooting, but it should not be the main
 * value shown to a normal user.
 */

function formatSignedWorkTimeImpact(minutes, sign, t) {
  const total = Math.round(toNumberOrNull(minutes) || 0);
  if (total <= 0) return EMPTY_VALUE;

  const prefix = sign === "positive" ? "+" : "−";
  return `${prefix}${formatMinutesAsHours(total, t)}`;
}

function formatTechnicalMinutes(minutes, t, locale) {
  const total = Math.round(toNumberOrNull(minutes) || 0);
  if (total <= 0) return EMPTY_VALUE;

  return t("workItems:details.duration.technicalMinutes", {
    minutes: new Intl.NumberFormat(locale).format(total),
  });
}

/**
 * Format full-time-equivalent leave days based on the hb_visits rule:
 * 480 minutes = 1 full-time day.
 *
 * This mirrors the business rule used by the Leave / HR module for balance
 * consumption. It is especially important for part-time employees, where the
 * scheduled absence can cover several employee-specific working days but still
 * consume fewer full-time-equivalent days from the statutory balance.
 */
function formatFteDaysFromMinutes(minutes, t, locale) {
  const total = toNumberOrNull(minutes);
  if (total == null || total <= 0) return EMPTY_VALUE;

  const fteDays = total / 480;
  return t("workItems:details.duration.fteDays", {
    days: new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(fteDays),
  });
}

/**
 * Return a local civil YYYY-MM-DD value for backend date/timestamp fields.
 *
 * Leave request timestamps are stored as technical instants, while the UI shows
 * civil business days in the browser/user timezone.  We therefore must not slice
 * the raw UTC string (for example `2026-07-27T22:00:00Z`), because that would
 * display the previous UTC day even though the local business day is 2026-07-28.
 */
function toLocalDateOnly(value) {
  if (!value) return "";

  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDaysToDateOnly(dateOnly, days) {
  const [year, month, day] = String(dateOnly || "").split("-").map(Number);
  if (!year || !month || !day) return dateOnly;

  const d = new Date(year, month - 1, day, 12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return toLocalDateOnly(d);
}

function isLocalMidnight(value) {
  if (!value) return false;

  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(d.getTime())) return false;

  return d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
}

function getBusinessEndDateOnly(startDate, endValue) {
  const endDate = toLocalDateOnly(endValue);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return endDate;

  // Full-day leave requests are commonly represented as half-open civil
  // intervals: [start, next-day-midnight).  In Poland/Europe this can arrive
  // from the backend as e.g. 2026-07-29T22:00:00Z, which is local 2026-07-30
  // 00:00.  For user-facing calendar days this means the last business day is
  // the previous local date.
  if (endDate > startDate && isLocalMidnight(endValue)) {
    return addDaysToDateOnly(endDate, -1);
  }

  return endDate;
}

function isDateOnly(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function getLeaveBusinessStartDateOnly(entity) {
  // Prefer normalized DTO fields when backend provides them. They are the
  // safest cross-page contract because they remove inclusive/exclusive-end and
  // timezone ambiguity from the frontend.
  const explicit = entity?.business_start_date || entity?.start_date || entity?.businessStartDate;
  if (isDateOnly(explicit)) return String(explicit);

  return toLocalDateOnly(entity?.start_at);
}

function getLeaveBusinessEndDateOnly(entity) {
  const explicit = entity?.business_end_date || entity?.end_date || entity?.businessEndDate;
  if (isDateOnly(explicit)) return String(explicit);

  const startDate = getLeaveBusinessStartDateOnly(entity);
  return getBusinessEndDateOnly(startDate, entity?.end_at || entity?.start_at);
}

function formatDateOnlyLabel(dateOnly, locale) {
  if (!isDateOnly(dateOnly)) return EMPTY_VALUE;
  const [year, month, day] = String(dateOnly).split("-").map(Number);
  const d = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (!Number.isFinite(d.getTime())) return EMPTY_VALUE;
  return d.toLocaleDateString(locale);
}

/**
 * Calculate inclusive calendar days between the business start/end dates.
 *
 * This deliberately uses the same local civil-date semantics as the visible
 * leave period.  It prevents a request displayed as 2026-07-28 – 2026-07-29
 * from being summarized as three calendar days just because the technical
 * backend end timestamp is the next local midnight.
 */
function getInclusiveCalendarDays(startValue, endValue) {
  const startDate = isDateOnly(startValue) ? String(startValue) : toLocalDateOnly(startValue);
  if (!isDateOnly(startDate)) return null;

  const endDate = isDateOnly(endValue)
    ? String(endValue)
    : getBusinessEndDateOnly(startDate, endValue || startValue);
  if (!isDateOnly(endDate)) return null;

  const [sy, sm, sd] = startDate.split("-").map(Number);
  const [ey, em, ed] = endDate.split("-").map(Number);
  const start = new Date(sy, sm - 1, sd, 12, 0, 0, 0);
  const end = new Date(ey, em - 1, ed, 12, 0, 0, 0);

  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;

  const diff = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  return diff > 0 ? diff : null;
}

function formatCalendarDays(count, t, locale) {
  const n = toNumberOrNull(count);
  if (n == null || n <= 0) return EMPTY_VALUE;

  return t("workItems:details.duration.calendarDays", {
    days: new Intl.NumberFormat(locale).format(n),
  });
}

/**
 * Resolve a stable leave type code for UI branching.
 *
 * The normal source is entity.leaveType.code. For older DTOs or snapshots we
 * can still fall back to the payroll absence code because many application
 * leave types use the same stable technical code there.
 */
function getLeaveTypeCode(entity) {
  return String(
    entity?.leaveType?.code ||
      entity?.leave_type_code ||
      entity?.payroll_absence_code_snapshot ||
      ""
  )
    .trim()
    .toUpperCase();
}

function getBalanceAccountingType(entity) {
  return String(
    entity?.leaveType?.balance_accounting_type ||
      entity?.balance_accounting_type ||
      ""
  )
    .trim()
    .toUpperCase();
}

function isTimeDebtLeave(entity) {
  const code = getLeaveTypeCode(entity);
  const balanceType = getBalanceAccountingType(entity);

  return (
    code === "PRIVATE_EXIT_TO_MAKE_UP" ||
    code === "MAKE_UP_WORK" ||
    balanceType === "ADD_TIME_DEBT" ||
    balanceType === "REDUCE_TIME_DEBT"
  );
}

function formatPayrollRate(value, locale) {
  const n = toNumberOrNull(value);
  if (n == null) return EMPTY_VALUE;

  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)}%`;
}

/**
 * Summary-card value renderer for durations.
 *
 * For normal leave, the main concept is usually full-time-equivalent balance
 * impact plus hours. For short work-time adjustments, the main concept is the
 * hour/minute interval. The raw minute value stays secondary in both cases.
 */
function buildDurationValue(entity, t, locale) {
  const minutes = toNumberOrNull(entity?.duration_minutes);
  if (minutes == null || minutes <= 0) return EMPTY_VALUE;

  const timeDebt = isTimeDebtLeave(entity);
  const hoursText = formatMinutesAsHours(minutes, t);
  const technicalText = formatTechnicalMinutes(minutes, t, locale);
  const fteText = formatFteDaysFromMinutes(minutes, t, locale);

  const primary = timeDebt ? hoursText : fteText;
  const secondaryParts = timeDebt
    ? [technicalText]
    : [hoursText, technicalText].filter(Boolean);

  return (
    <div className="wi-value-stack">
      <div className="wi-value-primary">{primary}</div>
      {secondaryParts.length > 0 && (
        <div className="wi-value-secondary">{secondaryParts.join(" · ")}</div>
      )}
    </div>
  );
}

function buildPeriodValue(entity, t, locale) {
  const startDate = getLeaveBusinessStartDateOnly(entity);
  const endDate = getLeaveBusinessEndDateOnly(entity);

  // Prefer normalized business dates when available/derivable. The imported
  // formatLeaveBusinessStart/End functions remain as a defensive fallback for
  // future DTOs that may include time-of-day semantics.
  const from = isDateOnly(startDate)
    ? formatDateOnlyLabel(startDate, locale)
    : formatLeaveBusinessStart(entity, locale);
  const to = isDateOnly(endDate)
    ? formatDateOnlyLabel(endDate, locale)
    : formatLeaveBusinessEnd(entity, locale);
  const calendarDays = getInclusiveCalendarDays(startDate, endDate);

  return (
    <div className="wi-value-stack">
      <div className="wi-value-primary">{from} – {to}</div>
      {calendarDays != null && (
        <div className="wi-value-secondary">{formatCalendarDays(calendarDays, t, locale)}</div>
      )}
    </div>
  );
}

function makeFact(label, value, options = {}) {
  if (value == null || value === "") return null;
  if (value === EMPTY_VALUE && options.hideEmpty) return null;
  return { label, value, ...options };
}

function compactFacts(facts) {
  return facts.filter(Boolean);
}

function buildLeaveAccountingFacts(entity, t, locale) {
  if (!entity) return [];

  const code = getLeaveTypeCode(entity);
  const balanceType = getBalanceAccountingType(entity);
  const minutes = toNumberOrNull(entity.duration_minutes);

  const consumedDays = toNumberOrNull(entity.entitlement_consumed_days);
  const consumedHours = toNumberOrNull(entity.entitlement_consumed_hours);
  const consumedCalendarDays = toNumberOrNull(entity.entitlement_consumed_calendar_days);

  const hasEntitlementConsumption =
    consumedDays != null || consumedHours != null || consumedCalendarDays != null;

  const balanceImpact = (() => {
    if (isTimeDebtLeave(entity)) {
      if (code === "PRIVATE_EXIT_TO_MAKE_UP" || balanceType === "ADD_TIME_DEBT") {
        return t("workItems:details.accounting.privateExitDecreasesWorkTimeBalance", {
          value: formatSignedWorkTimeImpact(minutes, "negative", t),
          defaultValue: "Decreases work-time balance: {{value}}",
        });
      }
      if (code === "MAKE_UP_WORK" || balanceType === "REDUCE_TIME_DEBT") {
        return t("workItems:details.accounting.makeUpIncreasesWorkTimeBalance", {
          value: formatSignedWorkTimeImpact(minutes, "positive", t),
          defaultValue: "Increases work-time balance: {{value}}",
        });
      }
    }

    if (balanceType === "DEDUCT_FROM_LEAVE_BALANCE") {
      return t("workItems:details.accounting.deductsLeaveBalance");
    }

    if (balanceType === "NO_BALANCE_EFFECT") {
      return t("workItems:details.accounting.noBalanceEffect");
    }

    return safeText(balanceType || entity?.leaveType?.balance_consumption_strategy || "");
  })();

  const entitlementConsumption = (() => {
    if (hasEntitlementConsumption) {
      const parts = [];
      if (consumedDays != null) {
        parts.push(t("workItems:details.accounting.consumedDays", {
          days: formatDecimal(consumedDays, 2, locale),
        }));
      }
      if (consumedHours != null) {
        parts.push(t("workItems:details.accounting.consumedHours", {
          hours: formatDecimal(consumedHours, 2, locale),
        }));
      }
      if (consumedCalendarDays != null) {
        parts.push(t("workItems:details.accounting.consumedCalendarDays", {
          days: formatDecimal(consumedCalendarDays, 2, locale),
        }));
      }
      return parts.join(" · ");
    }

    if (minutes != null && minutes > 0 && balanceType === "DEDUCT_FROM_LEAVE_BALANCE") {
      return formatFteDaysFromMinutes(minutes, t, locale);
    }

    return EMPTY_VALUE;
  })();

  return compactFacts([
    makeFact(t("workItems:details.fields.requestedUsageMode"), safeText(entity.requested_usage_mode)),
    makeFact(t("workItems:details.fields.balanceImpact"), balanceImpact),
    makeFact(t("workItems:details.fields.balanceConsumption"), entitlementConsumption, { hideEmpty: true }),
    makeFact(t("workItems:details.fields.payrollExport"), yesNo(Boolean(entity.payroll_export_enabled_snapshot), t)),
    makeFact(t("workItems:details.fields.payrollCode"), safeText(entity.payroll_absence_code_snapshot)),
    makeFact(t("workItems:details.fields.payrollRate"), formatPayrollRate(entity.payroll_payment_rate_percent_snapshot, locale)),
  ]);
}

function buildTimeSettlementFacts(entity, t, locale) {
  if (!entity || !isTimeDebtLeave(entity)) return [];

  const settlementStatus = entity.settlement_status || entity.time_settlement?.settlement_status;
  const settledMinutes = toNumberOrNull(entity.settled_minutes);
  const remainingMinutes = toNumberOrNull(entity.remaining_minutes);

  return compactFacts([
    makeFact(
      t("workItems:details.fields.settlementStatus"),
      settlementStatus
        ? t(`leaves:settlementStatus.${String(settlementStatus).toUpperCase()}`, { defaultValue: String(settlementStatus) })
        : EMPTY_VALUE
    ),
    makeFact(
      t("workItems:details.fields.settled"),
      settledMinutes != null ? formatMinutesAsHours(settledMinutes, t) : EMPTY_VALUE
    ),
    makeFact(
      t("workItems:details.fields.remaining"),
      remainingMinutes != null ? formatMinutesAsHours(remainingMinutes, t) : EMPTY_VALUE
    ),
  ]);
}

function getAllocationArray(container) {
  if (!container) return [];
  if (Array.isArray(container)) return container;
  if (Array.isArray(container.allocations)) return container.allocations;
  if (Array.isArray(container.items)) return container.items;
  return [];
}

function getPairedRequest(allocation) {
  return (
    allocation?.paired_leave_request ||
    allocation?.pairedLeaveRequest ||
    allocation?.source_leave_request ||
    allocation?.settlement_leave_request ||
    allocation?.sourceLeaveRequest ||
    allocation?.settlementLeaveRequest ||
    allocation?.request ||
    null
  );
}

function formatAllocationTitle(allocation, t, language, locale) {
  const paired = getPairedRequest(allocation);
  const leaveType = paired?.leaveType || paired?.leave_type || allocation?.leaveType || null;
  const leaveTypeName = getLocalizedLeaveTypeName(leaveType, language, t);
  const minutes = allocation?.minutes ?? allocation?.settled_minutes ?? allocation?.allocated_minutes;
  const date = paired?.start_at ? formatDateOnly(paired.start_at, locale) : null;

  return [date, leaveTypeName, minutes != null ? formatMinutesAsHours(minutes, t) : null]
    .filter(Boolean)
    .join(" · ");
}

function buildAllocationSections(entity, t, language, locale) {
  const settlement = entity?.time_settlement || null;
  if (!settlement) return [];

  const asDebt = getAllocationArray(settlement.as_debt);
  const asCredit = getAllocationArray(settlement.as_credit);

  return compactFacts([
    asDebt.length > 0
      ? {
          title: t("workItems:details.sections.compensatedBy"),
          items: asDebt.map((item, index) => ({
            key: `debt-${index}`,
            label: formatAllocationTitle(item, t, language, locale),
            meta: [item.settlement_mode, item.allocation_state].filter(Boolean).join(" · "),
          })),
        }
      : null,
    asCredit.length > 0
      ? {
          title: t("workItems:details.sections.appliedTo"),
          items: asCredit.map((item, index) => ({
            key: `credit-${index}`,
            label: formatAllocationTitle(item, t, language, locale),
            meta: [item.settlement_mode, item.allocation_state].filter(Boolean).join(" · "),
          })),
        }
      : null,
  ]);
}

/* -------------------------------------------------------------------------- */
/* Type-aware UI normalization                                                */
/* -------------------------------------------------------------------------- */

/**
 * Convert backend DTO into a UI model that the page can render consistently.
 *
 * Supported:
 * - todo
 * - leave
 *
 * Fallback:
 * - generic rendering for future work item types
 */
function buildWorkItemViewModel(data, t, language, locale) {
  const type = String(data?.type || "").trim().toLowerCase();
  const entity = data?.entity || null;

  if (!entity) {
    return {
      title: t("workItems:details.values.workItem"),
      summaryFacts: [],
      extraFacts: [],
      debugFacts: [],
    };
  }

  if (type === "todo") {
    return {
      title: entity.description || t("workItems:details.values.todo"),
      summaryFacts: [
        { label: t("workItems:details.fields.type"), value: t("workItems:details.values.todo") },
        { label: t("workItems:details.fields.private"), value: yesNo(Boolean(entity.is_private), t) },
        { label: t("workItems:details.fields.done"), value: yesNo(Boolean(entity.is_done), t) },
      ],
      extraFacts: [
        { label: t("workItems:details.fields.creator"), value: formatUserLabel(entity.creator, t) },
        { label: t("workItems:details.fields.assignee"), value: formatUserLabel(entity.assignee, t) },
        { label: t("workItems:details.fields.created"), value: formatDateTime(entity.createdAt || entity.created_at, locale) },
        { label: t("workItems:details.fields.updated"), value: formatDateTime(entity.updatedAt || entity.updated_at, locale) },
      ],
      debugFacts: [
        { label: t("workItems:details.fields.key"), value: safeText(data?.key) },
        { label: t("workItems:details.fields.backendType"), value: safeText(data?.type) },
        { label: t("workItems:details.fields.entityId"), value: safeText(entity?.id) },
      ],
    };
  }

  if (type === "leave") {
    const leaveTypeName = getLocalizedLeaveTypeName(entity?.leaveType, language, t);

    return {
      title: leaveTypeName,

      /**
       * The summary deliberately avoids backend-looking values such as
       * "duration_minutes = 10560" as the main message. Users need the civil
       * interpretation first: period, status and human duration. Technical
       * minutes remain available as a secondary support value.
       */
      summaryFacts: compactFacts([
        makeFact(t("workItems:details.fields.type"), t("workItems:details.values.leaveRequest")),
        makeFact(t("workItems:details.fields.status"), formatLeaveStatus(entity.status, t)),
        makeFact(t("workItems:details.fields.period"), buildPeriodValue(entity, t, locale)),
        makeFact(t("workItems:details.fields.duration"), buildDurationValue(entity, t, locale)),
      ]),

      extraFacts: compactFacts([
        makeFact(t("workItems:details.fields.requester"), formatUserLabel(entity.user, t)),
        makeFact(t("workItems:details.fields.approver"), formatUserLabel(entity.approver, t)),
        makeFact(t("workItems:details.fields.submitted"), formatDateTime(entity.submitted_at, locale)),
        makeFact(t("workItems:details.fields.decided"), formatDateTime(entity.decided_at, locale)),
      ]),

      accountingFacts: buildLeaveAccountingFacts(entity, t, locale),
      timeSettlementFacts: buildTimeSettlementFacts(entity, t, locale),
      allocationSections: buildAllocationSections(entity, t, language, locale),

      /**
       * Technical facts are still useful, but they are no longer part of the
       * normal business view. They are rendered in a collapsed details block.
       */
      debugFacts: compactFacts([
        makeFact(t("workItems:details.fields.key"), safeText(data?.key)),
        makeFact(t("workItems:details.fields.backendType"), safeText(data?.type)),
        makeFact(t("workItems:details.fields.entityId"), safeText(entity?.id)),
        makeFact(t("workItems:details.fields.leaveTypeId"), safeText(entity?.leave_type_id)),
        makeFact(t("workItems:details.fields.entitlementContextId"), safeText(entity?.entitlement_context_id)),
      ]),
    };
  }

  /**
   * Generic fallback for future work item types.
   *
   * This prevents the page from becoming unusable the moment you add another
   * backend-supported type and forget to create a dedicated UI branch here.
   */
  return {
    title:
      entity?.title ||
      entity?.description ||
      t("workItems:details.values.genericDetails", {
        type: type || t("workItems:details.values.workItemLower"),
      }),
    summaryFacts: [
      { label: t("workItems:details.fields.type"), value: safeText(data?.type || t("workItems:details.values.unknown")) },
      { label: t("workItems:details.fields.entityId"), value: safeText(entity?.id) },
    ],
    extraFacts: [],
    debugFacts: [
      { label: t("workItems:details.fields.key"), value: safeText(data?.key) },
      { label: t("workItems:details.fields.backendType"), value: safeText(data?.type) },
      { label: t("workItems:details.fields.entityId"), value: safeText(entity?.id) },
    ],
  };
}

/* -------------------------------------------------------------------------- */
/* Reusable UI fragments                                                      */
/* -------------------------------------------------------------------------- */

function FactGrid({ facts = [] }) {
  if (!Array.isArray(facts) || facts.length === 0) return null;

  return (
    <div className="wi-facts">
      {facts.map((fact, index) => (
        <div key={`${fact.label}-${index}`} className="wi-fact">
          <div className="wi-fact-label">{fact.label}</div>
          <div className="wi-fact-value">{fact.value}</div>
        </div>
      ))}
    </div>
  );
}

function MetaBlock({ facts = [] }) {
  if (!Array.isArray(facts) || facts.length === 0) return null;

  return (
    <div className="wi-meta">
      {facts.map((fact, index) => (
        <div key={`${fact.label}-${index}`}>
          <strong>{fact.label}:</strong> {fact.value}
        </div>
      ))}
    </div>
  );
}

function InfoGrid({ facts = [] }) {
  if (!Array.isArray(facts) || facts.length === 0) return null;

  return (
    <div className="wi-info-grid">
      {facts.map((fact, index) => (
        <div key={`${fact.label}-${index}`} className="wi-info-row">
          <div className="wi-info-label">{fact.label}</div>
          <div className="wi-info-value">{fact.value}</div>
        </div>
      ))}
    </div>
  );
}

function AllocationSections({ sections = [] }) {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  return (
    <div className="wi-allocation-sections">
      {sections.map((section) => (
        <div key={section.title} className="wi-allocation-section">
          <div className="wi-allocation-title">{section.title}</div>
          <div className="wi-allocation-list">
            {section.items.map((item) => (
              <div key={item.key} className="wi-allocation-item">
                <div className="wi-allocation-label">{item.label || EMPTY_VALUE}</div>
                {item.meta && <div className="wi-allocation-meta">{item.meta}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TechnicalDetails({ facts = [], rawEntity = null, t }) {
  const hasFacts = Array.isArray(facts) && facts.length > 0;
  const hasRawEntity = rawEntity != null;

  if (!hasFacts && !hasRawEntity) return null;

  return (
    <details className="wi-technical-details">
      <summary>{t("workItems:details.sections.technicalDetails")}</summary>

      <div className="wi-technical-body">
        <InfoGrid facts={facts} />

        {hasRawEntity && (
          <details className="wi-raw-json-disclosure">
            <summary>{t("workItems:details.sections.rawEntityDev")}</summary>

            <div className="wi-raw-json-body">
              <pre className="wi-raw-json">
                {JSON.stringify(rawEntity, null, 2)}
              </pre>
            </div>
          </details>
        )}
      </div>
    </details>
  );
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const WorkItemPage = () => {
  const navigate = useNavigate();
  const { key } = useParams();
  const { t, i18n } = useTranslation(["workItems", "common", "leaves"]);

  const { authState, authChecked, softLogout } = useContext(AuthContext);

  const workItemKey = useMemo(() => {
    return decodeWorkItemKeyParam(key);
  }, [key]);

  const currentLanguage = i18n.resolvedLanguage || i18n.language || "en";
  const locale = t("common:language.locale");

  /* ------------------------------------------------------------------------ */
  /* Refs                                                                      */
  /* ------------------------------------------------------------------------ */

  /**
   * Abort current details request whenever:
   * - work item key changes
   * - component unmounts
   */
  const abortRef = useRef(null);

  /**
   * Mounted guard prevents setState on unmounted component.
   */
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (abortRef.current) {
        abortRef.current.abort();
      }

      abortRef.current = null;
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* State                                                                     */
  /* ------------------------------------------------------------------------ */

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  /* ------------------------------------------------------------------------ */
  /* Authoritative details load                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Load details every time auth is ready and key changes.
   */
  useEffect(() => {
    if (!authChecked) return;
    if (!authState.isAuthenticated) return;

    if (!workItemKey) {
      setLoading(false);
      setError(t("workItems:details.errors.invalidKey"));
      setData(null);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      setLoading(true);
      setError("");
      setData(null);

      try {
        const result = await fetchWorkItem(workItemKey, {
          signal: controller.signal,
        });

        if (controller.signal.aborted || !mountedRef.current) return;

        setData(result);

        /**
         * Strategy A: mark as seen after successful details load.
         *
         * This is intentionally best-effort and non-blocking:
         * - it is idempotent
         * - page rendering must not depend on it
         */
        markWorkItemSeen(workItemKey).catch(() => {
          // intentionally ignored
        });
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isAuthFailure(err)) {
          softLogout(
            `WORK_ITEM_PAGE_AUTH_FAIL:${err?.code || err?.response?.status || "401"}`
          );
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.error("[WorkItemPage] fetchWorkItem failed:", err);
        }

        if (!mountedRef.current) return;

        setError(getLoadErrorMessage(err, t));
      } finally {
        if (!controller.signal.aborted && mountedRef.current) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      controller.abort();
    };
  }, [authChecked, authState.isAuthenticated, workItemKey, softLogout, t]);

  /* ------------------------------------------------------------------------ */
  /* Derived view model                                                        */
  /* ------------------------------------------------------------------------ */

  const entity = data?.entity || null;

  /**
   * Raw backend JSON is no longer rendered as a prominent business-section.
   *
   * It remains available inside the collapsed Technical details block because
   * this page is often used during support and implementation testing. Keeping
   * the JSON behind an explicit disclosure gives developers and superusers fast
   * access to the payload without forcing normal users to look at raw database
   * fields during everyday work.
   */
  const viewModel = useMemo(() => {
    return buildWorkItemViewModel(data, t, currentLanguage, locale);
  }, [data, t, currentLanguage, locale]);

  /**
   * We render embedded chat only when details loaded successfully.
   *
   * Why:
   * - avoids double-failure noise on 403/404/invalid key
   * - keeps details as the gatekeeper for whether the work item exists and is visible
   */
  const canRenderEmbeddedChat =
    Boolean(authChecked) &&
    Boolean(authState.isAuthenticated) &&
    !loading &&
    !error &&
    Boolean(data?.key) &&
    Boolean(workItemKey);

  const handleBackToDashboard = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  /* ------------------------------------------------------------------------ */
  /* Render gating                                                             */
  /* ------------------------------------------------------------------------ */

  if (!authChecked) {
    return (
      <div className="workitem-page">
        <div className="wi-card">
          <div className="wi-loading">{t("common:common.loading")}</div>
        </div>
      </div>
    );
  }

  if (!authState.isAuthenticated) {
    /**
     * ProtectedRoute should normally redirect before this.
     * This is only a defensive fallback.
     */
    return (
      <div className="workitem-page">
        <div className="wi-card wi-card-error">
          <div className="wi-error-title">{t("workItems:details.states.notAuthenticated")}</div>
          <div className="wi-error-text">{t("workItems:details.states.redirecting")}</div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="workitem-page">
      <div className="workitem-topbar">
        <button
          type="button"
          className="wi-btn wi-btn-secondary"
          onClick={handleBackToDashboard}
          aria-label={t("workItems:details.actions.backToDashboardAria")}
        >
          {t("workItems:details.actions.backWithArrow")}
        </button>

        <div className="workitem-topbar-title">
          <div className="workitem-topbar-kicker">{t("workItems:details.title")}</div>
          <div className="workitem-topbar-key" title={workItemKey}>
            {workItemKey || EMPTY_VALUE}
          </div>
        </div>
      </div>

      <div className="wi-split">
        {/* ------------------------------------------------------------------ */}
        {/* DETAILS                                                             */}
        {/* ------------------------------------------------------------------ */}
        <div className="wi-details">
          {loading && (
            <div className="wi-card">
              <div className="wi-loading">{t("common:common.loading")}</div>
            </div>
          )}

          {!loading && error && (
            <div className="wi-card wi-card-error">
              <div className="wi-error-title">{t("workItems:details.errors.genericTitle")}</div>
              <div className="wi-error-text">{error}</div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="wi-btn wi-btn-secondary"
                  onClick={handleBackToDashboard}
                >
                  {t("workItems:details.actions.goBack")}
                </button>

                <button
                  type="button"
                  className="wi-btn wi-btn-secondary"
                  onClick={() => window.location.reload()}
                >
                  {t("workItems:details.actions.reloadPage")}
                </button>
              </div>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="wi-card">
                <div className="wi-section-title">{t("workItems:details.sections.summary")}</div>
                <div className="wi-title">{viewModel.title || EMPTY_VALUE}</div>

                <FactGrid facts={viewModel.summaryFacts} />
              </div>

              <div className="wi-card wi-card-subtle">
                <div className="wi-section-title">{t("workItems:details.sections.details")}</div>
                <MetaBlock facts={viewModel.extraFacts} />
              </div>

              {Array.isArray(viewModel.accountingFacts) && viewModel.accountingFacts.length > 0 && (
                <div className="wi-card wi-card-subtle">
                  <div className="wi-section-title">{t("workItems:details.sections.leaveAccounting")}</div>
                  <InfoGrid facts={viewModel.accountingFacts} />
                </div>
              )}

              {Array.isArray(viewModel.timeSettlementFacts) && viewModel.timeSettlementFacts.length > 0 && (
                <div className="wi-card wi-card-subtle wi-card-time-settlement">
                  <div className="wi-section-title">{t("workItems:details.sections.timeSettlement")}</div>
                  <InfoGrid facts={viewModel.timeSettlementFacts} />
                  <AllocationSections sections={viewModel.allocationSections} />
                </div>
              )}

              <div className="wi-card wi-card-subtle">
                <TechnicalDetails
                  facts={viewModel.debugFacts}
                  rawEntity={entity}
                  t={t}
                />
              </div>
            </>
          )}
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* CHAT                                                                */}
        {/* ------------------------------------------------------------------ */}
        <div className="wi-chat">
          {canRenderEmbeddedChat ? (
            <WorkItemChatPanel
              workItemKey={workItemKey}
              variant="embedded"
            />
          ) : (
            <div className="wi-card wi-card-subtle">
              <div className="wi-section-title">{t("workItems:details.sections.chat")}</div>
              <div className="wi-meta">
                {loading
                  ? t("workItems:details.chat.loading")
                  : error
                    ? t("workItems:details.chat.unavailableDueToDetailsError")
                    : t("workItems:details.chat.unavailable")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkItemPage;


